import { useMutation } from '@tanstack/react-query';

import {
  type CameraScanEntryPayload,
  ID_SCAN_UNKNOWN_MESSAGE,
  type IdPhotoEnvelope,
  type IdPhotoPayload,
  MOBILE_CHECKOUT_CAMERA_SCAN_ENTRY_PATH,
  MOBILE_CHECKOUT_ID_PHOTO_PATH,
  type ScanEnvelope,
  type ScanGuestEntryResponse,
  type Surfaced,
  unwrapIdPhotoResult,
  unwrapScanEntry,
} from '@dutchie-pos/requests/checkin/camera-id-scan';

import { post } from 'src/utils/helpers/http';
import { useBaseAPIParams } from 'src/utils/hooks/useBaseAPIParams';

/**
 * Stamps the surface the server records this against. Set here and nowhere else.
 *
 * It no longer selects the location feature — the mobile-checkout routes below are what the server
 * authorizes as Mobile-only (leaflogix #22416). Kept on the body because the DTO still carries the
 * field, so dropping it would be a wire change this layer has no reason to make.
 */
const withSurface = <TPayload>(payload: TPayload): Surfaced<TPayload> => ({
  ...payload,
  Surface: 'MobileCheckout',
});

/**
 * The server answered and refused, with a reason: a banned customer, an unparseable payload, the
 * feature off for this surface. Nothing was checked in, and the reason is the only thing the
 * operator can act on, so it is safe to show verbatim.
 *
 * Distinct from a transport failure on purpose, exactly as on Register. A request that never came
 * back may still have checked someone in, so it must not be reported as a refusal — the caller has
 * to warn about the guest list instead.
 */
export class ScanEntryRefusedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ScanEntryRefusedError';
  }
}

/**
 * Answered on this call rather than broadcast: a phone is not on the register scan channel.
 *
 * The decoded AAMVA payload is the whole identification, so there is no second entry shape here —
 * and the typed-entry route this used to sit beside no longer exists server-side.
 *
 * Posts the mobile-checkout route, which is the whole of how the server knows to authorize this as
 * Mobile. Register's route would be refused here even with `Surface: 'MobileCheckout'` on the body.
 */
export const useCameraScanEntryMutation = () => {
  const baseAPIParams = useBaseAPIParams();

  return useMutation({
    mutationKey: ['camera-scan-entry'],
    mutationFn: async (payload: CameraScanEntryPayload) => {
      // Awaited outside the try, so a transport rejection propagates unchanged rather than being
      // relabelled as something the server said.
      const envelope = await post<ScanEnvelope<ScanGuestEntryResponse>>(
        MOBILE_CHECKOUT_CAMERA_SCAN_ENTRY_PATH,
        { ...baseAPIParams, ...withSurface(payload) },
        { ignoreFormat: true },
      );

      try {
        return unwrapScanEntry(envelope);
      } catch (error) {
        // Only `Result: false` is the server refusing and saying so. A success envelope with no
        // payload, or none at all, is not a refusal: something may have landed, so it reaches the
        // caller's guest-list warning instead.
        if (envelope?.Result === false) {
          throw new ScanEntryRefusedError(error instanceof Error ? error.message : ID_SCAN_UNKNOWN_MESSAGE);
        }

        throw error;
      }
    },
  });
};

/**
 * Saves the opted-in front-of-card photo, after the check-in has already landed.
 *
 * No envelope unwrapping and no refusal class, unlike the entry above: this endpoint answers with
 * the result directly, and `Persisted: false` with a `Failure` code is the server declining the
 * image rather than failing the request. The caller reads that code, because one value of it —
 * a photo of the back — is the only photo failure staff can actually fix.
 *
 * Nothing here can undo the check-in, so there is deliberately no retry policy and no rollback.
 *
 * On the mobile-checkout photo route, for the same reason as the entry above: the path is what the
 * server authorizes, so Register's would be refused regardless of the stamped surface.
 */
export const useIdPhotoMutation = () => {
  const baseAPIParams = useBaseAPIParams();

  return useMutation({
    mutationKey: ['id-photo'],
    mutationFn: async (payload: IdPhotoPayload) => {
      // Raw, like the entry call: a recoverable refusal answers `Result: false` *carrying* the
      // result, and the standard helper rejects that shape with only `Message` — discarding the
      // `Failure` code the step turns into "photograph the front". Same envelope, same unwrap and
      // same fail-closed rules as Register; only the surface differs.
      //
      // The route stays this layer's Mobile-only one: the server authorizes the surface by path
      // (leaflogix #22416), so the envelope correction must not reach for the shared constant.
      const envelope = await post<IdPhotoEnvelope>(
        MOBILE_CHECKOUT_ID_PHOTO_PATH,
        { ...baseAPIParams, ...withSurface(payload) },
        { ignoreFormat: true },
      );

      return unwrapIdPhotoResult(envelope);
    },
  });
};
